import React from 'react';
import { Box, VStack, Text } from '@gluestack-ui/themed';
import { ViewStyle } from 'react-native';
import {
  useReactTable, getCoreRowModel, OnChangeFn, RowSelectionState, ColumnDef, Table,
} from '@tanstack/react-table';
import { useDataTable } from './hooks/useDataTable';
import { useColumnResizing } from './hooks/useColumnResizing';
import { useDataTableColumns } from './DataTable/hooks/useDataTableColumns';
import TablePagination from './DataTable/components/TablePagination';
import TableHeader from './DataTable/components/TableHeader';
import TableRow from './DataTable/components/TableRow';
import DataTableFunctionBar from './DataTable/components/DataTableFunctionBar';
import type { DataTableProps, TableRowProps } from './DataTable/types';
import { ActionConfig } from './DataTable/actionTypes';
import { AdminObjectName, ColumnDefinition, AdminModelMap } from './DataColumnConfig';

/**
 * DataTable - A high-performance, generic table component for the Administration section.
 *
 * This component acts as an orchestrator that combines:
 * - Data fetching and state management via `useDataTable`.
 * - Dynamic column generation via `useDataTableColumns`.
 * - Responsive layout with built-in search, filtering, and pagination.
 *
 * It uses a "Grouped Config Objects" pattern to manage its extensive API cleanly.
 *
 * @param {DataTableProps} props - The configuration objects for data, display, actions, and callbacks.
 * @returns {React.ReactElement} The rendered table with toolbar and pagination.
 */
export const DataTable = <K extends AdminObjectName>(props: DataTableProps<K>) => {
  const {
    dataConfig,
    displayConfig,
    searchFilterConfig = {},
    actionConfig = {},
    callbacks = {},
    paginationSelectionConfig = {},
  } = props;

  const { objectName, dataCallback } = dataConfig;

  const {
    tableTitle,
    noTitle = false,
    columns,
    singleLine = true,
    resizable = true,
    minColumnWidth = 60,
    maxColumnWidth = 640,
    noPadding = false,
  } = displayConfig;

  const {
    showSearch = true,
    showStateFilter = true,
    searchMaxWidth = 600,
    hideFilterLabel = false,
  } = searchFilterConfig;

  const { actions, onAction, isActionRunning = false } = actionConfig;

  const { onSort } = callbacks;

  const { enableSelection = false } = paginationSelectionConfig;

  const tableState = useDataTable({
    dataConfig,
    displayConfig,
    searchFilterConfig,
    paginationSelectionConfig,
    callbacks,
  });

  const {
    currentPage,
    currentPageSize,
    sortField,
    sortDirection,
    rowSelection,
    setRowSelection,
    isSelectionMode,
    setIsSelectionMode,
    selectedStates,
    setSelectedStates,
    includeDeprecated,
    setIncludeDeprecated,
    displayData,
    tablePagination,
    isLoading,
    handleSearch,
    handlePageChange,
    handleSort,
    selectedRows,
    objectLifecycleConfig,
    TABLE_HEIGHTS,
    onClearSelection,
    onExitSelectionMode,
  } = tableState;

  const { columnWidths, handleResize } = useColumnResizing(columns, minColumnWidth, maxColumnWidth);

  // Wrap onAction to pass callbacks for clearing selection after bulk operations
  const wrappedOnAction = React.useCallback(
    (actionCode: string, data: unknown) => {
      if (onAction) {
        // Pass the callbacks as a third parameter
        onAction(actionCode, data, { onClearSelection, onExitSelectionMode });
      }
    },
    [onAction, onClearSelection, onExitSelectionMode],
  );

  // Group actions by position
  const { headerActions, bulkActions, rowActions } = React.useMemo(() => {
    const entries = Object.entries(actions || {}).map(([id, config]) => [
      id,
      {
        id,
        label: config.label || id,
        position: config.position || 'none',
        ...config,
      },
    ]) as [string, ActionConfig][];
    return {
      headerActions: entries.filter(([, a]) => a.position === 'header'),
      bulkActions: entries.filter(([, a]) => a.bulk),
      rowActions: entries.filter(([, a]) => a.position === 'row'),
    };
  }, [actions]);

  // Extract column definition logic to custom hook
  const columnDefs = useDataTableColumns({
    tableColumns: columns || {},
    includeDeprecated,
    handleSort,
    onSort,
    sortField,
    sortDirection,
    singleLine,
    enableSelection,
    isSelectionMode,
    isActionRunning,
    onAction: wrappedOnAction,
    rowActions,
    objectName,
  });

  const table = useReactTable({
    data: displayData,
    columns: columnDefs as unknown as ColumnDef<AdminModelMap[K], unknown>[],
    state: {
      sorting: sortField ? [{ id: sortField, desc: sortDirection === 'desc' }] : [],
      pagination: { pageIndex: currentPage - 1, pageSize: currentPageSize },
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection as OnChangeFn<RowSelectionState>,
    manualSorting: true,
    manualPagination: true,
    pageCount: tablePagination?.total ? Math.ceil(tablePagination.total / currentPageSize) : -1,
    getCoreRowModel: getCoreRowModel(),
  });

  React.useEffect(() => {
    if (dataCallback) dataCallback({ pagination: tablePagination, data: displayData });
  }, [displayData, tablePagination, dataCallback]);

  return (
    <VStack style={{ paddingBottom: TABLE_HEIGHTS.paddingVertical / 2 } as ViewStyle} position="relative">
      <Box
        px={noPadding ? 0 : '$6'}
        py={noTitle ? 0 : '$4'}
        mb="$2"
        borderBottomWidth={noTitle ? 0 : 1}
        borderBottomColor="$borderLight200"
      >
        <DataTableFunctionBar
          tableTitle={tableTitle || (objectName ? objectName.charAt(0).toUpperCase() + objectName.slice(1) : '')}
          noTitle={noTitle}
          objectName={objectName}
          isLoading={isLoading}
          searchConfig={{
            onSearch: handleSearch,
            showSearch,
            searchPlaceholder: 'Search',
            searchableFields: tableState.searchableFields || [],
            defaultSearchField: tableState.defaultSearchField || 'title',
            searchMaxWidth,
          }}
          filterConfig={{
            showStateFilter,
            lifecycleConfig: objectLifecycleConfig,
            selectedStates,
            onStatesChange: setSelectedStates,
            includeDeprecated,
            onIncludeDeprecatedChange: setIncludeDeprecated,
            hideFilterLabel,
          }}
          actionConfig={{ actions: headerActions, onAction: wrappedOnAction, isActionRunning }}
          selectionConfig={{
            selectedRows,
            bulkActions,
            onClearSelection,
            isSelectionMode,
            onToggleSelectionMode: (enabled: boolean) => {
              if (!enabled) {
                onClearSelection();
                onExitSelectionMode();
              }
              setIsSelectionMode(enabled);
            },
            enableSelection,
          }}
        />
      </Box>

      <Box style={{ width: '100%', overflow: 'hidden' } as ViewStyle} mt="$2" px={noPadding ? 0 : '$6'}>
        <TableHeader<K>
          table={table as unknown as Table<AdminModelMap[K]>}
          columnWidths={columnWidths}
          tableColumns={columns as Record<string, ColumnDefinition<AdminModelMap[K]>>}
          sortField={sortField}
          sortDirection={sortDirection}
          handleSort={handleSort}
          handleResize={handleResize}
          minColumnWidth={minColumnWidth}
          maxColumnWidth={maxColumnWidth}
          height={TABLE_HEIGHTS.tableHeader}
          singleLine={singleLine}
          resizable={resizable}
        />

        <Box style={{ opacity: isLoading || isActionRunning ? 0.4 : 1 } as ViewStyle}>
          {table.getRowModel().rows.length > 0 ? (
            table
              .getRowModel()
              .rows.map((row) => (
                <TableRow<K>
                  key={row.id}
                  row={row as unknown as TableRowProps<K>['row']}
                  columnWidths={columnWidths}
                  tableColumns={columns as Record<string, ColumnDefinition<AdminModelMap[K]>>}
                  minColumnWidth={minColumnWidth}
                  maxColumnWidth={maxColumnWidth}
                  height={TABLE_HEIGHTS.row}
                  singleLine={singleLine}
                />
              ))
          ) : !isLoading ? (
            <Text size="xs" flex={1} textAlign="center" mt="$2">
              No records found
            </Text>
          ) : (
            <Box minHeight="$8" />
          )}
        </Box>
      </Box>

      <TablePagination
        total={tablePagination?.total || 0}
        page={currentPage}
        pageSize={currentPageSize}
        onPageChange={handlePageChange}
        isLoading={isLoading}
        isActionRunning={isActionRunning}
        isSelectionMode={isSelectionMode}
        height={TABLE_HEIGHTS.pagination}
        noPadding={noPadding}
      />
    </VStack>
  );
};

export default DataTable;
